/**
 * Reusable tooltip styling utilities for consistent tooltip appearance across the application
 * 
 * This module provides standardized tooltip styling that ensures:
 * - High contrast ratios for accessibility
 * - Consistent font sizes and spacing
 * - Proper z-index layering
 * - Dark mode support
 * - Better positioning and overflow handling
 */

import { SxProps, Theme } from '@mui/material/styles'

// ============================================================================
// TOOLTIP STYLING CONSTANTS
// ============================================================================

export const TOOLTIP_STYLES = {
  // Enhanced font sizes for better readability
  fontSize: {
    small: '0.8125rem',    // 13px - for secondary text
    medium: '0.875rem',    // 14px - for main tooltip text
    large: '0.9375rem',    // 15px - for headings
  },
  
  // Improved color contrast
  colors: {
    light: {
      background: '#ffffff',
      text: '#212121',
      border: '#d0d0d0',
      secondaryText: '#424242',
    },
    dark: {
      background: '#1a1a1a',
      text: '#ffffff',
      border: '#555555',
      secondaryText: '#e0e0e0',
    }
  },
  
  // Enhanced shadows for better visibility
  shadows: {
    light: '0 4px 20px rgba(0, 0, 0, 0.15), 0 1px 4px rgba(0, 0, 0, 0.1)',
    dark: '0 4px 20px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3)',
  },
  
  // Consistent spacing
  spacing: {
    padding: '16px 20px',
    compactPadding: '12px 16px',
    borderRadius: '8px',
  },
  
  // Z-index for proper layering
  zIndex: 1500,
} as const

// ============================================================================
// TOOLTIP STYLE GENERATORS
// ============================================================================

/**
 * Generate standard tooltip styles with improved readability
 */
export const getTooltipStyles = (options: {
  compact?: boolean
  maxWidth?: number
  isDarkMode?: boolean
} = {}): SxProps<Theme> => {
  const { compact = false, maxWidth = 360, isDarkMode = false } = options
  
  const colors = isDarkMode ? TOOLTIP_STYLES.colors.dark : TOOLTIP_STYLES.colors.light
  const shadow = isDarkMode ? TOOLTIP_STYLES.shadows.dark : TOOLTIP_STYLES.shadows.light

  return {
    '& .MuiTooltip-popper': {
      zIndex: `${TOOLTIP_STYLES.zIndex} !important`,
    },
    '& .MuiTooltip-tooltip': {
      backgroundColor: `${colors.background} !important`,
      color: `${colors.text} !important`,
      border: `1px solid ${colors.border} !important`,
      boxShadow: `${shadow} !important`,
      fontSize: `${TOOLTIP_STYLES.fontSize.medium} !important`,
      maxWidth: `${maxWidth}px !important`,
      padding: `${compact ? TOOLTIP_STYLES.spacing.compactPadding : TOOLTIP_STYLES.spacing.padding} !important`,
      borderRadius: `${TOOLTIP_STYLES.spacing.borderRadius} !important`,
      lineHeight: '1.5 !important',
      backdropFilter: 'none !important',
      filter: 'none !important', // Remove any filters
    },
    // Higher specificity selectors to ensure our styles override Material-UI defaults
    '& .MuiTooltip-tooltip.MuiTooltip-tooltip': {
      backgroundColor: `${colors.background} !important`,
      color: `${colors.text} !important`,
      border: `1px solid ${colors.border} !important`,
      boxShadow: `${shadow} !important`,
    },
    // Additional specificity - target nested elements
    '& .MuiTooltip-tooltip *': {
      color: 'inherit !important',
    },
    // Force override for all text elements in tooltips
    '& .MuiTooltip-tooltip .MuiTypography-root': {
      color: `${colors.text} !important`,
    },
    '& .MuiTooltip-tooltip .MuiBox-root': {
      color: `${colors.text} !important`,
    },
    '& .MuiTooltip-arrow': {
      color: `${colors.background} !important`,
      '&::before': {
        border: `1px solid ${colors.border} !important`,
        backgroundColor: `${colors.background} !important`,
      }
    }
  }
}

/**
 * Generate standard tooltip props with improved positioning
 */
export const getTooltipProps = (options: {
  enterDelay?: number
  leaveDelay?: number
  placement?: 'top' | 'bottom' | 'left' | 'right'
} = {}) => {
  const { 
    enterDelay = 300, 
    leaveDelay = 200, 
    placement = 'top' 
  } = options
  
  return {
    arrow: true,
    enterDelay,
    leaveDelay,
    placement,
    PopperProps: {
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: 8,
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top', 'bottom', 'left', 'right'],
          },
        },
      ],
    },
  }
}

/**
 * Get text styles for tooltip content
 */
export const getTooltipTextStyles = (variant: 'heading' | 'body' | 'caption' = 'body'): SxProps<Theme> => {
  const fontSizeMap = {
    heading: TOOLTIP_STYLES.fontSize.large,
    body: TOOLTIP_STYLES.fontSize.medium,
    caption: TOOLTIP_STYLES.fontSize.small,
  }
  
  return {
    fontSize: fontSizeMap[variant],
    color: TOOLTIP_STYLES.colors.light.text,
    '@media (prefers-color-scheme: dark)': {
      color: TOOLTIP_STYLES.colors.dark.text,
    }
  }
}

/**
 * Get secondary text styles for tooltip content
 */
export const getTooltipSecondaryTextStyles = (): SxProps<Theme> => ({
  fontSize: TOOLTIP_STYLES.fontSize.small,
  color: TOOLTIP_STYLES.colors.light.secondaryText,
  '@media (prefers-color-scheme: dark)': {
    color: TOOLTIP_STYLES.colors.dark.secondaryText,
  }
})

// ============================================================================
// CONVENIENCE EXPORTS
// ============================================================================

/**
 * Standard tooltip configuration for most use cases
 */
export const standardTooltipConfig = {
  ...getTooltipProps(),
  sx: getTooltipStyles(),
}

/**
 * Compact tooltip configuration for smaller tooltips
 */
export const compactTooltipConfig = {
  ...getTooltipProps({ enterDelay: 200 }),
  sx: getTooltipStyles({ compact: true, maxWidth: 280 }),
}

/**
 * Large tooltip configuration for detailed content
 */
export const largeTooltipConfig = {
  ...getTooltipProps({ enterDelay: 400 }),
  sx: getTooltipStyles({ maxWidth: 400 }),
}
